/**
 * RDD (Report Data Dictionary) Parser
 *
 * Parses EnergyPlus .rdd files which contain available output variables
 * for a given model. The RDD file is generated after a simulation and lists
 * all reportable variables.
 *
 * Format example:
 * Output:Variable,*,Zone Mean Air Temperature,hourly; !- [C]
 * Output:Variable,*,Zone Air System Sensible Heating Energy,hourly; !- [J]
 */

import type { ReportFrequency } from './types';

/**
 * Represents an available output variable from the RDD file
 */
export interface RDDVariable {
  /** Variable name (e.g., "Zone Mean Air Temperature") */
  variableName: string;
  /** Key value pattern (e.g., "*" for all, or specific zone/surface names) */
  keyValue: string;
  /** Default reporting frequency */
  reportingFrequency: ReportFrequency;
  /** Units (e.g., "C", "W", "J") */
  units: string;
}

/**
 * Parsed RDD file data
 */
export interface RDDData {
  /** List of available output variables */
  variables: RDDVariable[];
}

/**
 * Parse report frequency string to enum
 */
function parseReportFrequency(freq: string): ReportFrequency {
  const normalized = freq.toLowerCase().trim();
  switch (normalized) {
    case 'timestep':
      return 'timestep';
    case 'hourly':
      return 'hourly';
    case 'daily':
      return 'daily';
    case 'monthly':
      return 'monthly';
    case 'runperiod':
      return 'runperiod';
    case 'environment':
      return 'environment';
    case 'annual':
      return 'annual';
    default:
      return 'hourly';
  }
}

/**
 * Parse a single RDD line in IDF format
 * Format: Output:Variable,*,Variable Name,frequency; !- [units]
 */
function parseRDDLineIDF(line: string): RDDVariable | null {
  const trimmed = line.trim();
  if (!trimmed.toLowerCase().startsWith('output:variable')) {
    return null;
  }

  // Extract units from comment if present
  let units = '';
  const unitsMatch = trimmed.match(/!\s*-?\s*\[([^\]]*)\]/);
  if (unitsMatch?.[1]) {
    units = unitsMatch[1];
  }

  // Remove the trailing comment for parsing
  const mainPart = trimmed.split('!')[0]?.trim() ?? '';

  // Remove trailing semicolon
  const withoutSemicolon = mainPart.replace(/;$/, '').trim();

  // Split by comma
  const parts = withoutSemicolon.split(',').map((p) => p.trim());

  // Expected format: Output:Variable, keyValue, variableName, frequency
  if (parts.length < 4) {
    return null;
  }

  const keyValue = parts[1] ?? '*';
  const variableName = parts[2] ?? '';
  const frequency = parts[3] ?? 'hourly';

  if (!variableName) {
    return null;
  }

  return {
    variableName,
    keyValue,
    reportingFrequency: parseReportFrequency(frequency),
    units,
  };
}

/**
 * Parse a single RDD line in CSV/Regular format
 * Format: VarType,ReportType,Variable Name [Units]
 * Example: Zone,Average,Site Outdoor Air Drybulb Temperature [C]
 */
function parseRDDLineCSV(line: string): RDDVariable | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  // Skip header lines
  if (trimmed.startsWith('Program Version') ||
      trimmed.startsWith('Var Type') ||
      trimmed.startsWith('!')) {
    return null;
  }

  // Split by comma - but be careful, variable names might contain commas in rare cases
  const parts = trimmed.split(',').map((p) => p.trim());

  if (parts.length < 3) {
    return null;
  }

  const varType = parts[0] ?? ''; // Zone, HVAC, Plant, etc.
  // parts[1] is reportType (Average, Sum, etc.) - not needed for our purposes
  const variableNameWithUnits = parts.slice(2).join(',').trim(); // Join remaining parts in case of commas

  if (!variableNameWithUnits) {
    return null;
  }

  // Extract units from variable name: "Variable Name [Units]"
  let variableName = variableNameWithUnits;
  let units = '';
  const unitsMatch = variableNameWithUnits.match(/^(.+?)\s*\[([^\]]*)\]\s*$/);
  if (unitsMatch) {
    variableName = unitsMatch[1]?.trim() ?? variableNameWithUnits;
    units = unitsMatch[2] ?? '';
  }

  return {
    variableName,
    keyValue: varType || '*', // Use varType as key hint
    reportingFrequency: 'hourly', // CSV format doesn't include frequency
    units,
  };
}

/**
 * Detect the RDD file format
 * Returns 'idf' for Output:Variable format, 'csv' for regular columnar format
 */
function detectRDDFormat(content: string): 'idf' | 'csv' {
  // Check first few lines to detect format
  const lines = content.split('\n').slice(0, 10);
  for (const line of lines) {
    if (line.trim().toLowerCase().startsWith('output:variable')) {
      return 'idf';
    }
    // CSV format has header like "Var Type (reported time step),Var Report Type,Variable Name"
    if (line.includes('Var Type') || line.includes('Var Report Type')) {
      return 'csv';
    }
  }
  // Default to CSV since that's what EnergyPlus produces by default
  return 'csv';
}

/**
 * Parse RDD file content
 * Automatically detects format (IDF-style or CSV-style)
 */
export function parseRDD(content: string): RDDData {
  const lines = content.split('\n');
  const variables: RDDVariable[] = [];

  const format = detectRDDFormat(content);
  const parser = format === 'idf' ? parseRDDLineIDF : parseRDDLineCSV;

  for (const line of lines) {
    const variable = parser(line);
    if (variable) {
      variables.push(variable);
    }
  }

  return { variables };
}

/**
 * Get unique variable names from RDD data
 */
export function getUniqueVariableNames(data: RDDData): string[] {
  const names = new Set<string>();
  for (const variable of data.variables) {
    names.add(variable.variableName);
  }
  return Array.from(names).sort();
}
